import { create } from 'zustand';
import { User } from '@/types';
import {
  getAuthToken,
  getCurrentUser,
  storeAuthData,
  clearAuth,
  StoredUser,
} from '@/utils/storage';

/**
 * Authentication store using Zustand
 * Replaces Angular's BehaviorSubject-based auth state in AuthService
 */

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  // Set user after successful login
  setUser: (user: User, token: string) => void;
  // Clear user on logout
  logout: () => void;
  // Initialize auth state from storage (on app load)
  initializeAuth: () => void;
  // Set loading state
  setLoading: (isLoading: boolean) => void;
  // Set error state
  setError: (error: string | null) => void;
  // Update user profile
  updateUser: (updates: Partial<User>) => void;
  // Clear error
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

/**
 * Convert StoredUser to User type
 */
const storedUserToUser = (storedUser: StoredUser): User => ({
  userId: storedUser.userId,
  userEmail: storedUser.userEmail,
  userName: storedUser.userName,
  userRole: storedUser.userRole as User['userRole'],
  token: storedUser.token,
});

/**
 * Convert User to StoredUser type
 */
const userToStoredUser = (user: User, token: string): StoredUser => ({
  userId: user.userId,
  userEmail: user.userEmail,
  userName: user.userName,
  userRole: user.userRole,
  token: token,
});

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true, // Start loading until we check storage
  error: null,

  // Actions
  setUser: (user: User, token: string) => {
    // Store in session storage
    storeAuthData(token, userToStoredUser(user, token));

    set({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  },

  logout: () => {
    clearAuth();
    // Wipe all persisted store data so stale workspace/org never shows on next login
    localStorage.removeItem('workspace-storage');
    localStorage.removeItem('auth-storage');
    sessionStorage.clear();

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  initializeAuth: () => {
    const token = getAuthToken();
    const storedUser = getCurrentUser();

    if (token && storedUser) {
      set({
        user: storedUserToUser(storedUser),
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } else {
      // Clear any inconsistent state
      clearAuth();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  setError: (error: string | null) => {
    set({ error, isLoading: false });
  },

  updateUser: (updates: Partial<User>) => {
    const currentUser = get().user;
    const currentToken = get().token;

    if (currentUser && currentToken) {
      const updatedUser = { ...currentUser, ...updates };

      // Update storage
      storeAuthData(currentToken, userToStoredUser(updatedUser, currentToken));

      set({ user: updatedUser });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));

/**
 * Selector hooks for specific state slices
 */
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);

export default useAuthStore;
